import { supabase } from '../supabaseClient';

const FUNCTION_NAME = 'map-tiles';
const RENEWAL_MARGIN_MS = 12 * 60 * 60 * 1000;
const RETRY_DELAY_MS = 5 * 60 * 1000;

export interface MapConfig {
  archives: Readonly<Record<string, string>>;
  expiresAt: string;
}

let cachedConfig: MapConfig | null = null;
let configPromise: Promise<MapConfig> | null = null;
let retryAfter = 0;

function isR2ArchiveUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:'
      && url.hostname.toLowerCase().endsWith('.r2.cloudflarestorage.com')
      && !url.username
      && !url.password
      && !url.port
      && !url.hash
      && url.pathname.split('/').filter(Boolean).length >= 2
      && !/\s/.test(value);
  } catch {
    return false;
  }
}

function normalizeConfig(payload: unknown): MapConfig {
  if (!payload || typeof payload !== 'object') {
    throw new Error('The map service returned an invalid reply.');
  }

  const response = payload as Record<string, unknown>;
  const expiresAt = String(response['expiresAt'] ?? '');
  const expiry = Date.parse(expiresAt);
  const rawArchives = response['archives'];
  if (!Number.isFinite(expiry) || expiry <= Date.now() || !rawArchives || typeof rawArchives !== 'object') {
    throw new Error('The map service returned an invalid reply.');
  }

  const archives: Record<string, string> = {};
  for (const [name, rawUrl] of Object.entries(rawArchives as Record<string, unknown>)) {
    const url = typeof rawUrl === 'string' ? rawUrl : '';
    if (!/^[a-z]+$/.test(name) || !isR2ArchiveUrl(url)) {
      throw new Error('The map service returned an invalid reply.');
    }
    archives[name] = url;
  }

  if (!archives['basemap']) {
    throw new Error('The map service returned an invalid reply.');
  }

  return { archives: Object.freeze(archives), expiresAt: new Date(expiry).toISOString() };
}

function isUsable(config: MapConfig | null, now = Date.now()): config is MapConfig {
  return Boolean(config && Date.parse(config.expiresAt) > now);
}

async function requestConfig(): Promise<MapConfig> {
  const { data, error } = await supabase.functions.invoke(FUNCTION_NAME);
  if (error) throw error;
  return normalizeConfig(data);
}

export const mapConfigService = {
  async getMapConfig({ forceRefresh = false }: { forceRefresh?: boolean } = {}): Promise<MapConfig> {
    const now = Date.now();
    const held = cachedConfig;
    if (isUsable(held, now) && !forceRefresh) {
      const comfortablyCurrent = Date.parse(held.expiresAt) - now > RENEWAL_MARGIN_MS;
      if (comfortablyCurrent || retryAfter > now) return held;
    }

    if (configPromise) return configPromise;

    configPromise = requestConfig()
      .then((config) => {
        cachedConfig = config;
        retryAfter = 0;
        return config;
      })
      .catch((error: unknown) => {
        if (isUsable(cachedConfig)) {
          retryAfter = Date.now() + RETRY_DELAY_MS;
          return cachedConfig;
        }
        throw error;
      });

    try {
      return await configPromise;
    } finally {
      configPromise = null;
    }
  },

  getCachedMapConfig(): MapConfig | null {
    return isUsable(cachedConfig) ? cachedConfig : null;
  },

  getRenewalDelay(config: MapConfig): number {
    return Math.max(1_000, Date.parse(config.expiresAt) - Date.now() - RENEWAL_MARGIN_MS);
  },
};
